import { X, Gauge, Zap, Droplets, LineChart as LineChartIcon, Cpu, Thermometer, Fuel, Route, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { getUserSettings } from "@/lib/userSettings";

const tz = () => getUserSettings().timezone || "Pacific/Noumea";
const fmtX = (t) => { try { return new Date(t).toLocaleTimeString("fr-FR", { timeZone: tz(), hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fmtFull = (t) => { try { return new Date(t).toLocaleString("fr-FR", { timeZone: tz() }); } catch { return ""; } };

function MiniChart({ meta, data }) {
  const Icon = meta.Icon;
  const pts = data.filter((p) => p[meta.key] != null).map((p) => ({ t: p.t, val: p[meta.key] }));
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-3" data-testid={`trip-chart-${meta.key}`}>
      <p className="flex items-center gap-1.5 text-xs font-bold mb-1.5" style={{ color: meta.color }}>
        <Icon className="w-3.5 h-3.5" /> {meta.label} <span className="text-muted-foreground font-normal">{meta.unit.trim()}</span>
      </p>
      <div className="h-40 w-full">
        {pts.length < 2 ? (
          <div className="h-full flex items-center justify-center text-muted-foreground text-xs">Pas assez de données CAN</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pts} margin={{ top: 6, right: 12, left: -10, bottom: 2 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="t" tickFormatter={fmtX} tick={{ fontSize: 10, fill: "#a1a1aa" }} minTickGap={40} />
              <YAxis tick={{ fontSize: 10, fill: "#a1a1aa" }} width={52} domain={["auto", "auto"]} tickFormatter={(v) => (typeof v === "number" ? Math.round(v * 10) / 10 : v)} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                labelFormatter={fmtFull} formatter={(v) => [`${v}${meta.unit}`, meta.label]} />
              <Line type="monotone" dataKey="val" stroke={meta.color} strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function Tile({ Icon, label, value, sub, color }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-2.5" data-testid={`can-stat-${label}`}>
      <p className="flex items-center gap-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide"><Icon className="w-3 h-3" style={{ color }} /> {label}</p>
      <p className="data font-bold text-base mt-0.5" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function TripChartsModal({ trip, title, onClose }) {
  const raw = (trip?.points || []).filter((p) => p.can);
  let fuelBase = null;
  for (const p of raw) { if (p.can.fuel_used_l != null) { fuelBase = p.can.fuel_used_l; break; } }
  const series = raw.map((p) => ({
    t: p.timestamp,
    rpm: p.can.rpm ?? null,
    throttle: p.can.throttle ?? null,
    coolant_temp: p.can.coolant_temp ?? null,
    can_speed: p.can.can_speed ?? null,
    // consommation DEPUIS le début du trajet (delta du compteur cumulé)
    fuel_used_l: (p.can.fuel_used_l != null && fuelBase != null) ? Math.round((p.can.fuel_used_l - fuelBase) * 10) / 10 : null,
    fuel_l: p.can.fuel_l ?? null,
    fuel_pct: p.can.fuel_pct ?? null,
  }));
  const has = (k) => series.some((p) => p[k] != null);

  // ---- Résumé chiffré CANbus du trajet ----
  const vals = (k) => raw.map((p) => p.can[k]).filter((v) => v != null);
  const avg = (a) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : null);
  const max = (a) => (a.length ? Math.max(...a) : null);
  const min = (a) => (a.length ? Math.min(...a) : null);
  const r = (v, d = 0) => (v == null ? null : Math.round(v * 10 ** d) / 10 ** d);

  const usedArr = vals("fuel_used_l");
  const CAN_KEYS = ["rpm", "throttle", "fuel_used_l", "fuel_l", "fuel_pct", "coolant_temp", "can_speed", "total_mileage_km"];
  const hasCanData = raw.some((p) => CAN_KEYS.some((k) => p.can[k] != null));
  let fuelConsumed = usedArr.length >= 2 ? usedArr[usedArr.length - 1] - usedArr[0] : null;
  if (fuelConsumed == null) { const lvl = vals("fuel_l"); if (lvl.length >= 2 && lvl[0] > lvl[lvl.length - 1]) fuelConsumed = lvl[0] - lvl[lvl.length - 1]; }
  const distKm = trip?.distance_km ?? (vals("total_mileage_km").length >= 2 ? max(vals("total_mileage_km")) - min(vals("total_mileage_km")) : null);
  const consoL100 = (fuelConsumed != null && distKm) ? (fuelConsumed / distKm) * 100 : null;

  const stats = !hasCanData ? [] : [
    fuelConsumed != null && { Icon: Droplets, label: "Carburant consommé", value: `${r(fuelConsumed, 1)} L`, color: "#14B8A6" },
    consoL100 != null && { Icon: Fuel, label: "Conso moyenne", value: `${r(consoL100, 1)} L/100km`, color: "#22C55E" },
    vals("rpm").length && { Icon: Gauge, label: "Régime moteur", value: `${r(avg(vals("rpm")))} tr/min`, sub: `max ${r(max(vals("rpm")))}`, color: "#0EA5E9" },
    vals("throttle").length && { Icon: Zap, label: "Accélérateur max", value: `${r(max(vals("throttle")))} %`, color: "#EAB308" },
    vals("can_speed").length && { Icon: TrendingUp, label: "Vitesse CAN max", value: `${r(max(vals("can_speed")))} km/h`, color: "#F5821F" },
    vals("coolant_temp").length && { Icon: Thermometer, label: "Temp. moteur", value: `${r(max(vals("coolant_temp")))} °C`, sub: `moy ${r(avg(vals("coolant_temp")))}`, color: "#EF4444" },
    distKm != null && { Icon: Route, label: "Distance", value: `${r(distKm, 1)} km`, color: "#8B5CF6" },
  ].filter(Boolean);

  const cons = has("fuel_used_l") ? { key: "fuel_used_l", label: "Carburant consommé (trajet)", unit: " L", color: "#14B8A6" }
    : has("fuel_l") ? { key: "fuel_l", label: "Carburant (niveau)", unit: " L", color: "#14B8A6" }
    : has("fuel_pct") ? { key: "fuel_pct", label: "Carburant", unit: " %", color: "#22C55E" } : null;
  const charts = [
    has("rpm") && { key: "rpm", label: "Régime moteur", unit: " tr/min", color: "#0EA5E9", Icon: Gauge },
    has("throttle") && { key: "throttle", label: "Accélérateur", unit: " %", color: "#EAB308", Icon: Zap },
    has("can_speed") && { key: "can_speed", label: "Vitesse CAN", unit: " km/h", color: "#F5821F", Icon: TrendingUp },
    has("coolant_temp") && { key: "coolant_temp", label: "Température moteur", unit: " °C", color: "#EF4444", Icon: Thermometer },
    cons && { ...cons, Icon: Droplets },
  ].filter(Boolean);

  const hasCan = stats.length > 0 || charts.length > 0;

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="trip-charts-modal">
      <div className="glass rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 animate-slidein border border-[#17A2B8]/40" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <Cpu className="w-5 h-5 brand-teal shrink-0" />
            <div className="min-w-0">
              <p className="font-bold text-base truncate">{title || "Infos CANbus du trajet"}</p>
              <p className="label-mono">Données bus véhicule · {series.length} point(s)</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="trip-charts-close" className="text-muted-foreground hover:text-foreground p-1.5 rounded-md hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        {!hasCan ? (
          <div className="h-40 flex flex-col items-center justify-center text-center text-muted-foreground gap-2" data-testid="trip-charts-empty">
            <Cpu className="w-8 h-8 opacity-40" />
            <p className="text-sm">Aucune donnée CAN (régime, accélérateur, carburant, température) sur ce trajet.</p>
            <p className="text-[11px]">Nécessite un adaptateur bus CAN (LVCAN200) câblé et actif sur le boîtier.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {stats.length > 0 && (
              <div>
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><LineChartIcon className="w-3.5 h-3.5 brand-teal" /> Résumé CANbus</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="can-summary">
                  {stats.map((s) => <Tile key={s.label} {...s} />)}
                </div>
              </div>
            )}
            {charts.length > 0 && (
              <div>
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Évolution le long du trajet</p>
                <div className="space-y-3" data-testid="trip-charts-grid">
                  {charts.map((m) => <MiniChart key={m.key} meta={m} data={series} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
